import { z } from 'zod';

import { mePayloadSchema } from '../../../access/api/schemas';
import { parseOrThrow } from '../../../api';
import { LANGUAGE_CHOICES } from '../../../i18n/languages';
import { sentTargetOf } from '../domain/sentTarget';
import { ALERT_CHANNELS, IDENTIFIER_KINDS, PASSCODE_CHANNELS } from '../types/domain.types';
import type { PasscodeChannel, SideBarItem } from '../types/domain.types';

const channel = z.enum(PASSCODE_CHANNELS);
const alertChannel = z.enum(ALERT_CHANNELS);
const seconds = z.number().int().nonnegative();
const count = z.number().int().positive();

const codeSentFields = {
  maskedTo: z.string(),
  expiresInSeconds: seconds,
  resendInSeconds: seconds,
  verifiesIdentifier: z.boolean(),
  codeLength: count,
};

const withSentTarget = <T extends { readonly sentOn: PasscodeChannel; readonly maskedTo: string }>(sent: T): T => ({
  ...sent,
  maskedTo: sentTargetOf(sent.sentOn, sent.maskedTo),
});

export const codeSentSchema = z.object({ sentOn: channel, ...codeSentFields }).transform(withSentTarget);

export const phoneCodeSentSchema = z.object({ sentOn: alertChannel, ...codeSentFields }).transform(withSentTarget);

export const emailCodeSentSchema = z.object({ sentOn: z.literal('EMAIL'), ...codeSentFields }).transform(withSentTarget);

const identitySchema = z.object({
  userId: z.string().min(1),
  userName: z.string().nullish(),
  email: z.string().nullish(),
  isStaff: z.boolean().default(false),
  roleKey: z.string().nullish(),
  adminGrant: z.string().nullish(),
});

const sideBarItemSchema: z.ZodType<SideBarItem> = z.lazy(() =>
  z
    .object({
      name: z.string(),
      link: z.string().optional(),
      children: z.array(sideBarItemSchema).optional(),
    })
    .passthrough(),
);

const layoutInfoSchema = z.object({
  sideBar: z.array(sideBarItemSchema),
  landingPage: z.string().min(1),
});

export const sessionOpenedSchema = z
  .object({
    token: z.string().min(1),
    identity: identitySchema.nullish(),
    layoutInfo: layoutInfoSchema.nullish(),
    passwordGrant: z.string().min(1).nullish(),
    grantExpiresInSeconds: count.nullish(),
    access: mePayloadSchema.nullish().catch(null),
    language: z.enum(LANGUAGE_CHOICES).nullish().catch(null),
  })
  .refine((dto) => !dto.passwordGrant || typeof dto.grantExpiresInSeconds === 'number', 'a password grant must say when it expires');
export type SessionOpenedDto = z.infer<typeof sessionOpenedSchema>;

export const loginTokenPayloadSchema = z.object({
  userId: z.string().min(1),
  userName: z.string().default(''),
  email: z.string().default(''),
  isStaff: z.boolean().default(false),
});

export const identifyResultSchema = z.object({
  kind: z.enum(IDENTIFIER_KINDS),
  masked: z.string(),
  channels: z.array(channel),
  passwordSet: z.boolean(),
  phoneVerified: z.boolean(),
  emailVerified: z.boolean(),
});

export const passwordSetSchema = z.object({ passwordSet: z.literal(true) });

export const resetGrantSchema = z
  .object({ passwordGrant: z.string().min(1), grantExpiresInSeconds: count })
  .transform((grant) => ({ token: grant.passwordGrant, expiresInSeconds: grant.grantExpiresInSeconds }));

export const phoneVerifiedSchema = z.object({
  number: z.string().min(1),
  isPhoneNumberVerified: z.literal(true),
  realtimeChannel: alertChannel,
  moved: z.boolean(),
});

export const emailVerifiedSchema = z.object({
  email: z.string().min(1),
  isEmailAddressVerified: z.literal(true),
});

export const channelSwitchedSchema = z.object({
  realtimeChannel: alertChannel,
});

export { parseOrThrow };
